import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import { Location } from '@grandvisionhq/api-client';
import { Util, Autocomplete, Button, RichTextArea } from '@grandvisionhq/common';
import { Container, Heading } from '@grandvisionhq/elements';
import { Icon } from '@grandvisionhq/icons';
import { useIntl, useApiConfig } from '@grandvisionhq/state';

const getPlacePredictions = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(place1, url) {
        const { places  } = yield Location.getPlacePredictions(url, {
            place: place1
        });
        return places.map((place, index)=>{
            const [primaryText, secondaryText] = place;
            return {
                id: index.toString(),
                title: primaryText !== null && primaryText !== void 0 ? primaryText : '',
                subTitle: secondaryText !== null && secondaryText !== void 0 ? secondaryText : ''
            };
        });
    });
    return function getPlacePredictions(place1, url) {
        return _ref.apply(this, arguments);
    };
}();
const regHex = /#([a-f0-9]{3}){1,2}\b/i;
const regPresetBg = /black|grey|white/;
const BookAppointment = ({ link , inputField , title , isLocationEnabled , isPlacePredictionsEnabled , imageUrl , text , backgroundColor , textColor ,  })=>{
    const { getLabel  } = useIntl();
    const { locationsUrl  } = useApiConfig();
    const [inputValue, setInputValue] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSuggestionSelected, setIsSuggestionSelected] = React.useState(false);
    const [predictions, setPredictions] = React.useState([]);
    const [bookAppointmentUrl, setBookAppointmentUrl] = React.useState();
    const autoCompleteLabels = {
        placeholder: inputField.placeholder,
        noResults: getLabel('stores.page.autocomplete.noResults'),
        noAvailableResultsScreenReaderHint: getLabel('elements.autocomplete.noAvailableResultsScreenReaderHint'),
        submitButtonScreenReaderHint: getLabel('stores.actions.useMyLocation')
    };
    const onClickSuggestion = React.useCallback((id)=>{
        const selectedSuggestion = predictions.find((prediction)=>prediction.id === id
        );
        if (selectedSuggestion) {
            const value = `${selectedSuggestion.title}, ${selectedSuggestion.subTitle}`;
            setIsSuggestionSelected(true);
            setInputValue(value);
            if (link.url) {
                const destinationUrl = new URL(link.url, window.origin);
                const separator = destinationUrl.href.includes('?') ? '&' : '?';
                window.location.href = `${destinationUrl.href}${separator}search=${encodeURIComponent(value)}`;
            }
        }
    }, [
        link.url,
        predictions
    ]);
    const onChange = React.useCallback((input)=>{
        setInputValue(input.currentTarget.value);
        setIsSuggestionSelected(false);
    }, []);
    const onCurrentLocationSubmit = React.useCallback(()=>{
        const destinationUrl = new URL(link.url, window.location.origin);
        destinationUrl.searchParams.set('userLocation', 'true');
        window.location.href = destinationUrl.href;
    }, [
        link
    ]);
    React.useEffect(()=>{
        setBookAppointmentUrl(new URL(link.url, window.location.origin));
    }, [
        link.url
    ]);
    React.useEffect(()=>{
        if (Boolean(inputValue.trim()) && bookAppointmentUrl) {
            bookAppointmentUrl === null || bookAppointmentUrl === void 0 ? void 0 : bookAppointmentUrl.searchParams.set('search', inputValue);
        } else if (bookAppointmentUrl === null || bookAppointmentUrl === void 0 ? void 0 : bookAppointmentUrl.searchParams.has('search')) {
            bookAppointmentUrl === null || bookAppointmentUrl === void 0 ? void 0 : bookAppointmentUrl.searchParams.delete('search');
        }
        let current = true;
        let timeout;
        if (inputValue.length < 3 || isSuggestionSelected || !isPlacePredictionsEnabled) {
            setPredictions([]);
        } else {
            setIsLoading(true);
            timeout = setTimeout(()=>{
                if (current) {
                    getPlacePredictions(inputValue, locationsUrl).then((result)=>{
                        if (current) {
                            setIsLoading(false);
                            setPredictions(result);
                        }
                    }).catch((err)=>console.error(err)
                    );
                }
            }, 400);
        }
        return ()=>{
            current = false;
            setIsLoading(false);
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [
        bookAppointmentUrl,
        inputValue,
        isPlacePredictionsEnabled,
        isSuggestionSelected,
        locationsUrl, 
    ]);
    const extended = !!imageUrl && !!text;
    const extendedBookAppointmentStyle = {};
    if (backgroundColor) {
        extendedBookAppointmentStyle.backgroundColor = regHex.test(backgroundColor) ? backgroundColor : regPresetBg.test(backgroundColor) ? Util.colorHexMap[backgroundColor] : 'transparent';
    }
    const textColorStyle = {};
    if (textColor) {
        textColorStyle.color = regHex.test(textColor) ? textColor : regPresetBg.test(textColor) ? Util.colorHexMap[textColor] : 'black';
    }
    return /*#__PURE__*/ React.createElement(Container, {
        className: classnames('book-appointment-container', {
            'book-appointment-container--extended': extended
        })
    }, extended && imageUrl && /*#__PURE__*/ React.createElement("img", {
        className: "book-appointment-image",
        src: imageUrl,
        alt: ""
    }), /*#__PURE__*/ React.createElement("div", {
        className: "book-appointment",
        style: extendedBookAppointmentStyle
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "small",
        className: classnames('book-appointment__title', {
            'book-appointment__title--extended': extended
        }),
        style: textColorStyle
    }, title), /*#__PURE__*/ React.createElement(Autocomplete, {
        id: inputField.id,
        name: "store-search",
        type: inputField.type,
        className: "book-appointment__input",
        labels: autoCompleteLabels,
        loading: isLoading,
        value: inputValue,
        showClearButton: false,
        submitIcon: isLocationEnabled ? /*#__PURE__*/ React.createElement(Icon, {
            name: "gps"
        }) : undefined,
        minCountCharactersToSubmit: 2,
        isSuggestionSelected: isSuggestionSelected,
        suggestions: predictions,
        inputDataT: "search",
        onChange: onChange,
        onSubmit: onCurrentLocationSubmit,
        onClear: ()=>({})
        ,
        onSuggestionClick: onClickSuggestion
    }), /*#__PURE__*/ React.createElement(Button, {
        className: "book-appointment__button",
        caption: link.label,
        color: link.style || 'primary',
        href: bookAppointmentUrl ? bookAppointmentUrl.toString().replace(/\+/g, ' ') : ''
    }), extended && text && /*#__PURE__*/ React.createElement("div", {
        className: "book-appointment__content",
        style: textColorStyle
    }, /*#__PURE__*/ React.createElement(RichTextArea, {
        content: text,
        textColor: textColor === 'white' ? 'white' : 'black'
    }))));
};

export { BookAppointment as default };
//# sourceMappingURL=BookAppointment.js.map
